/**
 * ministryPostureEngine.js
 *
 * Resolves the active ministry posture — what Kevia is DOING pastorally.
 *
 * DISTINCT from:
 *   — pastoralTimingEngine (FASE 6): WHEN to act (sequence decision)
 *   — wisdomResponseShaper (FASE 6): HOW the response sounds (tone)
 *   — spiritualPresenceEngine (FASE 7): WHO Kevia is (identity in moment)
 *
 * Posture = the active pastoral role Kevia takes when it speaks.
 *
 * Postures:
 *   acompañar           — walking alongside without imposing
 *   guiar               — offering direction with clarity
 *   sostener            — holding steady through a long process
 *   escuchar            — active listening before responding
 *   exhortar_suavemente — a gentle, warm invitation toward growth
 *   aclarar             — bringing precision and clarity
 *   consolar            — offering comfort and presence
 *   celebrar            — genuinely sharing in good news
 */

const POSTURE_INSTRUCTIONS = {
  acompañar:           null,          // default — no extra instruction needed
  guiar:               null,
  sostener:            "Sostén en el proceso. No apresures la resolución.",
  escuchar:            "Escucha activamente antes de responder. La comprensión precede a la guía.",
  exhortar_suavemente: null,
  aclarar:             null,
  consolar:            "Consuela con presencia genuina. Menos análisis, más cercanía.",
  celebrar:            null,
};

/**
 * @param {{
 *   emotion:   object|null,
 *   mood:      string,
 *   intent:    string,
 *   timing:    string,
 *   riskLevel: string,
 * }} params
 * @returns {{ posture: string, instruction: string|null }}
 */
export function resolveMinistryPosture({ emotion, mood, intent, timing, riskLevel }) {
  // Grief context → consolar
  if (mood === "grief" || emotion?.dominantEmotion === "grief") {
    return { posture: "consolar", instruction: POSTURE_INSTRUCTIONS.consolar };
  }

  // Timing: hold → sostener (active sustained presence)
  if (timing === "hold") {
    return { posture: "sostener", instruction: POSTURE_INSTRUCTIONS.sostener };
  }

  // Timing: question first → escuchar (understand before guiding)
  if (timing === "question_first") {
    return { posture: "escuchar", instruction: POSTURE_INSTRUCTIONS.escuchar };
  }

  // Celebration context → celebrar
  if (mood === "celebration") {
    return { posture: "celebrar", instruction: POSTURE_INSTRUCTIONS.celebrar };
  }

  // High / critical risk → sostener (don't rush to resolve)
  if (riskLevel === "high" || riskLevel === "critical") {
    return { posture: "sostener", instruction: POSTURE_INSTRUCTIONS.sostener };
  }

  // Analysis / planning → aclarar (precision and clarity)
  if (intent === "analysis" || intent === "planning") {
    return { posture: "aclarar", instruction: POSTURE_INSTRUCTIONS.aclarar };
  }

  // Accompany first timing → acompañar
  if (timing === "accompany_first") {
    return { posture: "acompañar", instruction: POSTURE_INSTRUCTIONS.acompañar };
  }

  // Pastoral followup, visit → guiar
  if (intent === "family_followup" || intent === "visit_plan") {
    return { posture: "guiar", instruction: POSTURE_INSTRUCTIONS.guiar };
  }

  return { posture: "acompañar", instruction: POSTURE_INSTRUCTIONS.acompañar };
}
